/**
 * Per-target fill orchestration — ephemeral working sets, sequential virtual occupancy.
 */

import { getBestCourtAssignmentsTwoStage } from '../twoStageEngine.js';
import {
  buildUpcomingEligiblePool,
  buildUpcomingSessionState,
  planUpcomingSlot,
  buildPlayerCategoriesFromPlayersById,
} from '../upcomingMatchPlanning.js';
import { getPlayersPerMatch, getPlayersPerSide } from '../competitiveMatchFormat.js';
import { buildOccupiedPlayerIds, planScheduledPlacements } from '../scheduledPlacement.js';
import { getRelaxationPreset } from '../relaxationPresets.js';
import {
  buildWaitlistPlayerIds,
  buildCourtAssignments,
  buildNextUpSlots,
  getTargetSlotTeams,
  getPlayersById,
  buildSessionPlayerStatusMap,
} from './v1StateAssembly.js';
import { MATCHMAKING_CONFIG } from '../matchmakingConfig.js';

const SERVER_ALGORITHM_NAME = 'two_stage_server';

/**
 * Keep the first `cap` waitlist ids (already ordered by waitlist_position).
 * Finishing / on-court players are merged after this slice.
 * @param {string[]} waitlistIds
 * @param {number} [cap]
 * @returns {string[]}
 */
export function capWaitlistForMatchmaking(waitlistIds, cap = MATCHMAKING_CONFIG.waitlistSearchCap) {
  if (!Array.isArray(waitlistIds) || waitlistIds.length === 0) return [];
  const limit = Number.isFinite(cap) && cap > 0 ? cap : MATCHMAKING_CONFIG.waitlistSearchCap;
  if (waitlistIds.length <= limit) return waitlistIds;
  return waitlistIds.slice(0, limit);
}

function waitlistForFill(v1) {
  return capWaitlistForMatchmaking(buildWaitlistPlayerIds(v1));
}

/**
 * @param {Array<{ court_type: string, index: number }>} targets
 */
export function sortExecutionTargets(targets) {
  return [...targets].sort((a, b) => {
    if (a.court_type !== b.court_type) {
      return a.court_type === 'active' ? 1 : -1;
    }
    return a.index - b.index;
  });
}

function buildFillMeta(result, target, slotIndex) {
  return {
    slotIndex: target.court_type === 'next_up' ? slotIndex : undefined,
    targetKind: target.court_type,
    targetIndex: target.index,
    team1: result.teams.team1,
    team2: result.teams.team2,
    gameCreationAlgorithm: SERVER_ALGORITHM_NAME,
    type: 'matchmaking',
    selectedScheduledGame: null,
    matchmakingRelaxed: result.rawCourt?.matchmakingRelaxed ?? false,
    matchmakingRelaxationDetails: result.rawCourt?.matchmakingRelaxationDetails ?? null,
    matchmakingDebugInfo: result.rawCourt?.matchmakingDebugInfo ?? null,
  };
}

function buildScheduledFillMeta(placement, target) {
  return {
    slotIndex: target.court_type === 'next_up' ? placement.slotIndex : undefined,
    targetKind: target.court_type,
    targetIndex: target.index,
    team1: placement.team1,
    team2: placement.team2,
    gameCreationAlgorithm: SERVER_ALGORITHM_NAME,
    type: 'scheduled',
    selectedScheduledGame: placement.selectedScheduledGame ?? null,
    matchmakingRelaxed: false,
    matchmakingRelaxationDetails: null,
  };
}

/**
 * @param {object} v1
 * @param {{ court_type: string, index: number }} target
 * @param {object} ctx
 */
function fillNextUpTarget(v1, target, ctx) {
  const {
    virtuallyOccupied,
    priorFills,
    sessionState,
    callMatchmakingFn,
    debug,
    debugSlots,
  } = ctx;

  const slotIndex = target.index;
  const playersById = getPlayersById(v1);
  const activeCourts = buildCourtAssignments(v1, 'active');
  const nextUpSlots = buildNextUpSlots(v1);
  const slotShell = nextUpSlots.find((s) => s.slotIndex === slotIndex);
  if (slotShell && !slotShell.isEmpty) return null;

  const matchFormat = v1.session?.competitive_match_format ?? 'doubles';
  const gameTypeRestriction = v1.session?.game_type_restriction ?? 'all_types';
  const playersPerMatch = getPlayersPerMatch(matchFormat);
  const cooldownEnabled = v1.session?.cooldown_enabled === true;
  const partnerships = v1.state?.partnerships ?? [];
  const scheduledGames = v1.state?.scheduled_games ?? [];
  const playerWaitMetrics = v1.state?.player_wait_metrics ?? {};
  const playerCategories = buildPlayerCategoriesFromPlayersById(playersById);
  const sessionPlayerStatus = buildSessionPlayerStatusMap(v1);

  const finishingIds = (v1.state?.excluded_player_ids || []).filter(Boolean);
  const waitlist = waitlistForFill(v1);
  const effectivePool = [...new Set([...waitlist, ...finishingIds])].filter(
    (id) => !virtuallyOccupied.has(id),
  );

  const baseState = {
    activeCourts,
    upcomingSlots: nextUpSlots.filter((s) => !s.isEmpty),
  };

  const emptySlotIndices = [slotIndex];
  const scheduledForSlot = priorFills.filter(
    (f) => f.type === 'scheduled' && f.slotIndex === slotIndex,
  );

  if (scheduledForSlot.length === 0) {
    const { scheduledPlacements } = planScheduledPlacements(
      baseState,
      emptySlotIndices,
      scheduledGames,
      playersById,
    );
    const hit = scheduledPlacements.find((p) => p.slotIndex === slotIndex);
    if (hit) {
      return buildScheduledFillMeta(hit, target);
    }
  }

  const allPlacements = priorFills
    .filter((f) => f.targetKind === 'next_up')
    .map((f) => ({
      slotIndex: f.slotIndex ?? f.targetIndex,
      team1: f.team1,
      team2: f.team2,
    }));

  const occupied = buildOccupiedPlayerIds(baseState, allPlacements);
  virtuallyOccupied.forEach((id) => occupied.add(id));

  const virtualNextSlots = [
    ...nextUpSlots.filter((s) => !s.isEmpty && s.slotIndex !== slotIndex),
    ...allPlacements.map((f) => ({
      slotIndex: f.slotIndex,
      team1: f.team1,
      team2: f.team2,
    })),
  ];

  const slotWaitlist = effectivePool.filter((id) => !occupied.has(id));

  const pool = buildUpcomingEligiblePool({
    waitingList: slotWaitlist,
    courtAssignments: activeCourts,
    nextAssignments: virtualNextSlots,
    playersById,
    sessionPlayerStatus,
    scheduledGames,
    sessionPlayerScheduledGames: {},
    playerWaitMetrics,
    playersPerMatch,
    matchFormat,
    hardExcludeIds: [],
    partnerships,
    cooldownEnabled,
  });

  const result = planUpcomingSlot(pool, sessionState, {
    playersById,
    partnerships,
    playerWaitMetrics,
    gameTypeRestriction,
    playerCategories,
    matchFormat,
    playersPerMatch,
    callMatchmakingFn,
  });

  if (debug && debugSlots) {
    debugSlots.push({
      phase: 'matchmaking',
      slotIndex,
      target,
      slotWaitlist,
      eligiblePool: pool.eligibleIds,
      usingFallbackPool: pool.usingFallbackPool === true,
    });
  }

  if (!result.teams) return null;
  return buildFillMeta(result, target, slotIndex);
}

/**
 * @param {object} v1
 * @param {{ court_type: string, index: number }} target
 * @param {object} ctx
 */
function fillActiveTarget(v1, target, ctx) {
  const {
    virtuallyOccupied,
    sessionState,
    callMatchmakingFn,
  } = ctx;

  const courtNumber = target.index;
  const playersById = getPlayersById(v1);
  const matchFormat = v1.session?.competitive_match_format ?? 'doubles';
  const gameTypeRestriction = v1.session?.game_type_restriction ?? 'all_types';
  const playersPerMatch = getPlayersPerMatch(matchFormat);
  const playersPerSide = getPlayersPerSide(matchFormat);
  const cooldownEnabled = v1.session?.cooldown_enabled === true;
  const partnerships = v1.state?.partnerships ?? [];
  const scheduledGames = v1.state?.scheduled_games ?? [];
  const playerWaitMetrics = v1.state?.player_wait_metrics ?? {};
  const playerCategories = buildPlayerCategoriesFromPlayersById(playersById);
  const sessionPlayerStatus = buildSessionPlayerStatusMap(v1);

  const existing = getTargetSlotTeams(v1, target);
  const hasPartial = existing.team1.length > 0 || existing.team2.length > 0;
  if (hasPartial) {
    if (existing.team1.length >= playersPerSide && existing.team2.length >= playersPerSide) {
      return null;
    }
  }

  const activeCourts = buildCourtAssignments(v1, 'active').filter(
    (c) => Number(c.courtNumber) !== courtNumber,
  );
  const nextUpSlots = buildNextUpSlots(v1);

  const finishingIds = (v1.state?.excluded_player_ids || []).filter(Boolean);
  const waitlist = waitlistForFill(v1);
  const firstNext = nextUpSlots.find((s) => s.slotIndex === 0);
  const nextOnCourtPlayers = firstNext
    ? [...(firstNext.team1 || []), ...(firstNext.team2 || [])]
    : [];

  let effectivePool = [...new Set([...waitlist, ...finishingIds, ...nextOnCourtPlayers])];
  effectivePool = effectivePool.filter((id) => !virtuallyOccupied.has(id));

  const occupied = buildOccupiedPlayerIds(
    {
      activeCourts,
      upcomingSlots: nextUpSlots.filter((s) => !s.isEmpty),
    },
    [],
  );
  virtuallyOccupied.forEach((id) => occupied.add(id));
  existing.team1.forEach((id) => occupied.add(id));
  existing.team2.forEach((id) => occupied.add(id));

  const slotWaitlist = effectivePool.filter((id) => !occupied.has(id));
  const virtualNextSlots = nextUpSlots.filter((s) => !s.isEmpty && s.slotIndex !== 0);

  const pool = buildUpcomingEligiblePool({
    waitingList: slotWaitlist,
    courtAssignments: activeCourts,
    nextAssignments: virtualNextSlots,
    playersById,
    sessionPlayerStatus,
    scheduledGames: hasPartial ? [] : scheduledGames,
    sessionPlayerScheduledGames: {},
    playerWaitMetrics,
    playersPerMatch,
    matchFormat,
    hardExcludeIds: [],
    partnerships,
    cooldownEnabled,
  });

  const result = planUpcomingSlot(pool, sessionState, {
    playersById,
    partnerships,
    playerWaitMetrics,
    gameTypeRestriction,
    playerCategories,
    matchFormat,
    playersPerMatch,
    callMatchmakingFn,
  });

  if (!result.teams) return null;

  if (hasPartial) {
    const newTeam1 = [...existing.team1];
    const newTeam2 = [...existing.team2];
    result.teams.team1.forEach((id) => {
      if (!newTeam1.includes(id) && newTeam1.length < playersPerSide) newTeam1.push(id);
    });
    result.teams.team2.forEach((id) => {
      if (!newTeam2.includes(id) && newTeam2.length < playersPerSide) newTeam2.push(id);
    });
    return {
      ...buildFillMeta({ ...result, teams: { team1: newTeam1, team2: newTeam2 } }, target, null),
      partialMerge: true,
    };
  }

  return buildFillMeta(result, target, null);
}

/**
 * @param {object} v1 — MatchmakingInputV1
 * @param {{ prng?: () => number, debug?: boolean }} [options]
 */
export function runAllTargetFills(v1, { prng = null, debug = false } = {}) {
  const targets = sortExecutionTargets(v1.execution?.targets || []);
  const playersById = getPlayersById(v1);
  const waitlist = buildWaitlistPlayerIds(v1);
  const finishingIds = (v1.state?.excluded_player_ids || []).filter(Boolean);
  const effectivePool = [...new Set([...waitlist, ...finishingIds])];

  const relaxationConfig = getRelaxationPreset(v1.session?.relaxation_preset ?? 'default');
  const sessionPlayerStatus = buildSessionPlayerStatusMap(v1);
  const sessionState = buildUpcomingSessionState({
    playersById,
    poolPlayerIds: effectivePool,
    courtAssignments: buildCourtAssignments(v1, 'active'),
    gameHistory: v1.state?.match_history ?? [],
    playerInteractions: v1.state?.player_interactions ?? {},
    totalGamesCompleted: v1.state?.total_games_completed ?? 0,
    playerWaitMetrics: v1.state?.player_wait_metrics ?? {},
    matchPreferencesEnabled: v1.session?.match_preferences_enabled !== false,
    cooldownEnabled: v1.session?.cooldown_enabled === true,
    partnerships: v1.state?.partnerships ?? [],
    scheduledGames: v1.state?.scheduled_games ?? [],
    relaxationConfig,
    sessionPlayerStatus,
    pendingFinishGame: v1.state?.pending_finish_game ?? null,
    excludedPlayerIds: finishingIds,
  });

  if (prng) sessionState._prng = prng;

  const matchFormat = v1.session?.competitive_match_format ?? 'doubles';
  const gameTypeRestriction = v1.session?.game_type_restriction ?? 'all_types';
  const playerCategories = buildPlayerCategoriesFromPlayersById(playersById);

  const callMatchmakingFn = (
    playerIds,
    numCourts,
    state,
    pById,
    partns,
    schedGames,
    restriction,
    categories,
    mmOptions = {},
  ) =>
    getBestCourtAssignmentsTwoStage(
      playerIds,
      numCourts,
      state,
      pById,
      false,
      partns,
      state.playerWaitMetrics || {},
      schedGames,
      restriction,
      categories,
      matchFormat,
      { fullWaitlistPlayerIds: mmOptions.fullWaitlistPlayerIds },
    );

  const virtuallyOccupied = new Set();
  const fills = [];
  const debugSlots = debug ? [] : null;

  const ctx = {
    virtuallyOccupied,
    priorFills: fills,
    sessionState,
    callMatchmakingFn,
    debug,
    debugSlots,
  };

  for (const target of targets) {
    let fill = null;
    if (target.court_type === 'next_up') {
      fill = fillNextUpTarget(v1, target, ctx);
    } else if (target.court_type === 'active') {
      fill = fillActiveTarget(v1, target, ctx);
    }

    if (fill) {
      fills.push(fill);
      [...(fill.team1 || []), ...(fill.team2 || [])].forEach((id) => {
        if (id) virtuallyOccupied.add(id);
      });
    }
  }

  return {
    fills,
    debugInfo: debug
      ? {
          targets,
          playerCategories: playerCategories ? Object.keys(playerCategories).length : 0,
          slots: debugSlots,
        }
      : null,
  };
}
